import { useMemo } from "react";
import {
	AccountBillToDialogFormContainer,
	AccountBillToDialogTitle,
	AccountInfoContainer,
	AllFieldsText,
	ErrorWrapper,
	HeaderTitleStyles,
	StartOverContinueContainer
} from "./NewOrders.styles";
import { Content } from "pages/Replacements/components/AddNewAddressModal/addNewAddressModal.styles.ts";
import {
	AllFieldsRequiredText,
	ContinueButtonText,
	ImportAddOnErrorText,
	SelectAccountAndBillingText,
	StartOverText
} from "constants/text";
import { Button } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "stores/application.store";
import AccountBillToContent from "./AccountBillToContent";
import { getGroupedConfigurations, useCheckForBillToAccountErrors } from "components/NewOrders/utils/NewOrderUtils.tsx";
import { newOrderActions } from "features/reducers/newOrder/newOrder.ts";
import { NewOrderCancelButton } from "./constants.ts";
import { models } from "types/api/viewModels.ts";
import AccountInfo from "components/NewOrders/components/AccountInfo/AccountInfo.tsx";
import ErrorBanner from "../Common/ErrorBanner/ErrorBanner.tsx";
import { getOrderAccountInfo } from "pages/OrderPages/utils/OrderDetailUtil.ts";
import { useDialog } from "hooks/useDialog.ts";
import { useUserLocation } from "hooks/useUserLocation.ts";

interface AccountBillToDialogFormProps {
	handleFindOriginalOrder: () => void;
	handleModalContinue: () => void;
	csvContents: models["ValidatedOrderViewModel"][] | undefined;
	isAddOn?: boolean;
	orderDetails?: models["OrderDetailViewModel"];
}

const AccountBillToDialogForm = ({
	handleFindOriginalOrder,
	handleModalContinue,
	csvContents,
	isAddOn,
	orderDetails
}: AccountBillToDialogFormProps) => {
	const dispatch = useDispatch();
	const { isNewOrderBuildOrderPage, isAddOnBuildOrderPage } = useUserLocation();
	const productLineAccountInfo = useSelector((state: RootState) => state.newOrder.productLineAccounts);
	const orderAccount = getOrderAccountInfo(orderDetails);
	const { closeDialog } = useDialog();

	const isValidForAddOn = useMemo(() => {
		if (!isAddOn) return true;
		const importedCSVProductLines: Set<string | null | undefined> = new Set(
			csvContents?.flatMap((csv) => csv?.configurations).map((config) => config?.globals?.productLine?.code) ?? []
		);
		const filteredProductLines = Object.entries(productLineAccountInfo).filter(([productLine]) =>
			importedCSVProductLines.has(productLine)
		);
		const billTosByProductLine = filteredProductLines.map(([, accounts]) =>
			accounts?.flatMap((account) => account?.billTos)
		);
		const isValidBillTo = billTosByProductLine.every((productLine) =>
			productLine?.find((billTo) => billTo?.billToId === orderDetails?.billToId)
		);
		const isValidAccount = filteredProductLines.every(([, accounts]) =>
			accounts?.find((account) => account.accountNumber === orderAccount?.accountNumber)
		);

		return isValidAccount && isValidBillTo;
	}, [csvContents, isAddOn, orderAccount?.accountNumber, orderDetails?.billToId, productLineAccountInfo]);

	const addOnAccount = useMemo(
		() =>
			Object.entries(productLineAccountInfo)
				.flatMap(([, accounts]) => accounts)
				.find((account) => account?.accountNumber === orderAccount?.accountNumber),
		[orderAccount?.accountNumber, productLineAccountInfo]
	);

	const addOnBillTo = useMemo(
		() => addOnAccount?.billTos?.find?.((billTo) => billTo.billToId === orderDetails?.billToId),
		[addOnAccount?.billTos, orderDetails?.billToId]
	);

	const checkForErrors = useCheckForBillToAccountErrors(handleModalContinue, csvContents);

	const handleCancel = () => {
		dispatch(newOrderActions.clearTempParsedCSV());
		closeDialog();
	};

	return (
		<AccountBillToDialogFormContainer>
			<HeaderTitleStyles>
				<AccountBillToDialogTitle>{SelectAccountAndBillingText}</AccountBillToDialogTitle>
				<AllFieldsText className="body2">{AllFieldsRequiredText}</AllFieldsText>
			</HeaderTitleStyles>
			{!isValidForAddOn && (
				<ErrorWrapper>
					<ErrorBanner
						errorMessaging={
							<div>
								<div>{ImportAddOnErrorText}</div>
							</div>
						}
					/>
				</ErrorWrapper>
			)}
			<Content>
				{csvContents &&
					Object.entries(getGroupedConfigurations(csvContents)).map(([productLine, CSVs]) => (
						<AccountBillToContent
							key={productLine}
							csvContents={CSVs}
							productLine={productLine}
							isAddOn={isAddOn}
							addOnAccount={addOnAccount}
							addOnBillTo={addOnBillTo}
						/>
					))}
				{isAddOn && (
					<AccountInfoContainer>
						<AccountInfo
							account={addOnAccount}
							orderDetails={orderDetails}
							variant="ACCOUNT"
						/>
						<AccountInfo
							billTo={addOnBillTo}
							orderDetails={orderDetails}
							variant="BILLTO"
						/>
					</AccountInfoContainer>
				)}
				<StartOverContinueContainer>
					{!isNewOrderBuildOrderPage && !isAddOnBuildOrderPage ? (
						<Button
							onClick={handleFindOriginalOrder}
							variant={isAddOn && !isValidForAddOn ? "contained" : "outlined"}
							fullWidth
						>
							{StartOverText}
						</Button>
					) : (
						<Button
							onClick={handleCancel}
							variant={isAddOn && !isValidForAddOn ? "contained" : "outlined"}
							fullWidth
						>
							{NewOrderCancelButton}
						</Button>
					)}
					{(isValidForAddOn || !isAddOn) && (
						<Button
							onClick={checkForErrors}
							variant="contained"
							fullWidth
						>
							{ContinueButtonText}
						</Button>
					)}
				</StartOverContinueContainer>
			</Content>
		</AccountBillToDialogFormContainer>
	);
};

export default AccountBillToDialogForm;
